import { useEffect, useRef } from 'react';
import { ConnectType, WalletStatus, useWallet } from '@terra-money/wallet-provider';
import { KEPLR_IDENTIFIER, isKeplrInAppBrowser } from '../wallets/keplr';

/**
 * Connects Keplr automatically inside the Keplr app's own browser.
 *
 * There the user arrived from their wallet and it is the only wallet present,
 * so a picker offering one option is pure friction. Deliberately limited to
 * `mode === 'mobile-web'`: with the desktop extension, Keplr sits alongside
 * Station and WalletConnect and the user should choose.
 *
 * Runs at most once. If the user disconnects, they meant it.
 */
export function useKeplrAutoConnect() {
    const wallet = useWallet();
    const status = wallet?.status;
    const connect = wallet?.connect;
    const attempted = useRef(false);

    useEffect(() => {
        if (attempted.current) return undefined;
        if (typeof connect !== 'function') return undefined;
        // Wait for the controller to finish deciding what is available.
        if (status !== WalletStatus.WALLET_NOT_CONNECTED) return undefined;

        // window.keplr is normally injected before this runs, but the in-app
        // browser does not guarantee it. Give it a short window rather than
        // deciding once and leaving the user staring at a Connect button.
        let tries = 0;
        const attempt = () => {
            if (attempted.current) return true;
            if (!isKeplrInAppBrowser()) return false;
            attempted.current = true;
            connect(ConnectType.PLUGINS, KEPLR_IDENTIFIER);
            return true;
        };

        if (attempt()) return undefined;

        const timer = setInterval(() => {
            tries += 1;
            if (attempt() || tries >= 10) clearInterval(timer);
        }, 250);
        return () => clearInterval(timer);
    }, [status, connect]);
}
